import React from 'react'
import {
  Tr,
  Td,
  useDisclosure,
  Collapse,
  Box,
  Text,
  Flex,
  IconButton,
  Badge,
  Button,
  HStack,
  Tooltip,
  Table,
  Thead,
  Tbody,
  Th,
  Divider,
  Link,
  Tag,
} from '@chakra-ui/react'
import { FiChevronDown, FiChevronUp, FiTrash2, FiEdit, FiExternalLink } from 'react-icons/fi'

const formatarData = (dataString) => {
  if (!dataString) return ''

  try {
    const data = new Date(dataString)
    return data.toLocaleDateString('pt-BR')
  } catch (e) {
    return dataString
  }
}

const StatusBadge = ({ status }) => {
  let colorScheme

  switch (status.toLowerCase()) {
    case 'em andamento':
      colorScheme = 'blue'
      break
    case 'aguardando pagamento':
      colorScheme = 'yellow'
      break
    case 'entregue':
      colorScheme = 'green'
      break
    case 'cancelado':
      colorScheme = 'red'
      break
    case 'em produção':
      colorScheme = 'purple'
      break
    default:
      colorScheme = 'gray'
  }

  return (
    <Badge colorScheme={colorScheme} px={2} py={1} borderRadius="md">
      {status}
    </Badge>
  )
}

const LinhaPedidoSpotComponent = ({ pedido, onExcluir }) => {
  const { isOpen, onToggle } = useDisclosure()

  const handleToggle = () => {
    onToggle()
  }

  return (
    <>
      <Tr>
        <Td borderRight="1px" borderColor="gray.100" borderBottom="none">
          {formatarData(pedido.data_pedido)}
        </Td>
        <Td borderRight="1px" borderColor="gray.100" borderBottom="none">
          <Badge colorScheme="purple" fontSize="0.9em" px={2} py={1}>
            {pedido.numero_pedido_spot}
          </Badge>
        </Td>
        <Td borderRight="1px" borderColor="gray.100" borderBottom="none">
          <Badge colorScheme="blue" fontSize="0.9em" px={2} py={1}>
            <Link href={pedido.link_pedido_compra} isExternal color="blue.500" display="flex" alignItems="center">
              {pedido.numero_pedido_innovation} <FiExternalLink style={{ marginLeft: '4px' }} />
            </Link>
          </Badge>
        </Td>
        <Td borderRight="1px" borderColor="gray.100" borderBottom="none">
          <Text>{pedido.responsavel_compra || "Não informado"}</Text>
        </Td>
        <Td borderRight="1px" borderColor="gray.100" borderBottom="none">
          <Text>{pedido.empresa || "Não informado"}</Text>
        </Td>
        <Td borderRight="1px" borderColor="gray.100" borderBottom="none">
          <Tooltip label={pedido.cnpj_fornecedor} placement="top">
            <Text>{pedido.fornecedor}</Text>
          </Tooltip>
        </Td>
        <Td isNumeric borderRight="1px" borderColor="gray.100" borderBottom="none">
          {typeof pedido.valor_total === 'number' ? pedido.valor_total.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : 'N/A'}
        </Td>
        <Td borderRight="1px" borderColor="gray.100" borderBottom="none">
          <StatusBadge status={pedido.status} />
        </Td>
        <Td borderRight="1px" borderColor="gray.100" borderBottom="none">
          <HStack spacing={2}>
            {/* <Tooltip label="Editar pedido">
              <IconButton aria-label="Editar pedido" icon={<FiEdit />} size="sm" colorScheme="blue" variant="outline" />
            </Tooltip> */}
            <Tooltip label="Excluir pedido">
              <IconButton aria-label="Excluir pedido" icon={<FiTrash2 />} size="sm" colorScheme="red" variant="outline" onClick={onExcluir} />
            </Tooltip>
          </HStack>
        </Td>
      </Tr>
      <Tr>
        <Td colSpan={9} p={0} borderTop="none">
          <Collapse in={isOpen} animateOpacity>
            <Box p={4} bg="white" borderTop="1px" borderColor="gray.100">
              <Flex direction="column" gap={4}>
                <Text fontWeight="bold" fontSize="lg">
                  Detalhes do Pedido
                </Text>

                <Flex justifyContent="space-between" flexWrap="wrap" gap={4}>
                  <Box minW="250px" flex="1">
                    <Text fontWeight="medium">Informações Gerais</Text>
                    <Divider my={2} />

                    <Flex justify="space-between" mb={2}>
                      <Text fontWeight="medium">Data do Pedido:</Text>
                      <Text>{formatarData(pedido.data_pedido)}</Text>
                    </Flex>

                    <Flex justify="space-between" mb={2}>
                      <Text fontWeight="medium">Previsão de Entrega:</Text>
                      <Text>{formatarData(pedido.data_previsao_entrega) || 'Não informado'}</Text>
                    </Flex>

                    <Flex justify="space-between" mb={2}>
                      <Text fontWeight="medium">Fornecedor:</Text>
                      <Text>{pedido.fornecedor}</Text>
                    </Flex>

                    {pedido.cnpj_fornecedor && (
                      <Flex justify="space-between" mb={2}>
                        <Text fontWeight="medium">CNPJ:</Text>
                        <Text>{pedido.cnpj_fornecedor}</Text>
                      </Flex>
                    )}

                    {pedido.transportadora && (
                      <Flex justify="space-between" mb={2}>
                        <Text fontWeight="medium">Transportadora:</Text>
                        <Text>{pedido.transportadora}</Text>
                      </Flex>
                    )}

                    {pedido.codigo_rastreio && (
                      <Flex justify="space-between" mb={2}>
                        <Text fontWeight="medium">Código de Rastreio:</Text>
                        <Text>{pedido.codigo_rastreio}</Text>
                      </Flex>
                    )}
                  </Box>

                  <Box minW="250px" flex="1">
                    <Text fontWeight="medium">Dados Financeiros</Text>
                    <Divider my={2} />

                    <Flex justify="space-between" mb={2}>
                      <Text fontWeight="medium">Valor Total:</Text>
                      <Text fontWeight="bold">
                        {typeof pedido.valor_total === 'number'
                          ? pedido.valor_total.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
                          : 'Não informado'}
                      </Text>
                    </Flex>

                    {pedido.forma_pagamento && (
                      <Flex justify="space-between" mb={2}>
                        <Text fontWeight="medium">Forma de Pagamento:</Text>
                        <Text>{pedido.forma_pagamento}</Text>
                      </Flex>
                    )}

                    <Flex justify="space-between" mb={2}>
                      <Text fontWeight="medium">Status:</Text>
                      <StatusBadge status={pedido.status} />
                    </Flex>

                    {pedido.responsavel_compra && (
                      <Flex justify="space-between" mb={2}>
                        <Text fontWeight="medium">Responsável:</Text>
                        <Text>{pedido.responsavel_compra}</Text>
                      </Flex>
                    )}

                    {pedido.link_pedido_compra && (
                      <Flex justify="space-between" mb={2}>
                        <Text fontWeight="medium">Link do Pedido:</Text>
                        <Link href={pedido.link_pedido_compra} isExternal color="blue.500" display="flex" alignItems="center">
                          Acessar <FiExternalLink style={{ marginLeft: '4px' }} />
                        </Link>
                      </Flex>
                    )}
                  </Box>
                </Flex>

                <Box mt={2}>
                  <Text fontWeight="medium" mb={2}>
                    Itens do Pedido
                  </Text>
                  <Table size="sm" variant="simple">
                    <Thead bg="gray.50">
                      <Tr>
                        <Th>Item</Th>
                        <Th isNumeric>Quantidade</Th>
                        <Th>Status</Th>
                        <Th>Detalhes</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {pedido.itens.map((item) => (
                        <Tr key={item.id}>
                          <Td>{item.nome}</Td>
                          <Td isNumeric>{item.quantidade}</Td>
                          <Td>
                            <StatusBadge status={item.status || 'Não informado'} />
                          </Td>
                          <Td>
                            <Flex gap={1} flexWrap="wrap">
                              {item.tipo_linha && (
                                <Tag size="sm" colorScheme="gray">
                                  {item.tipo_linha}
                                </Tag>
                              )}
                              {item.aguardando_arte && (
                                <Tag size="sm" colorScheme="orange">
                                  Aguardando Arte
                                </Tag>
                              )}
                              {item.amostra && (
                                <Tag size="sm" colorScheme="purple">
                                  Amostra
                                </Tag>
                              )}
                            </Flex>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>

                {pedido.observacoes && (
                  <Box mt={2}>
                    <Text fontWeight="medium">Observações:</Text>
                    <Box p={2} bg="gray.50" borderRadius="md" mt={1}>
                      <Text>{pedido.observacoes}</Text>
                    </Box>
                  </Box>
                )}

                {/* <Flex justify="flex-end" mt={2}>
                  <Button colorScheme="blue" size="sm" leftIcon={<FiEdit />}>
                    Editar Pedido
                  </Button>
                </Flex> */}
              </Flex>
            </Box>
          </Collapse>
        </Td>
      </Tr>
    </>
  )
}

export const LinhaPedidoSpot = React.memo(LinhaPedidoSpotComponent)
